import type { LabelRegion } from '../lib/api.ts'
import { Button } from './Button.tsx'
import { icons } from './icons.ts'

/**
 * A field's regions, listed and editable.
 *
 * The screen used to offer one button per field -- *ver* -- which showed
 * every region that field had, together, and said nothing about how many
 * there were or where. That is enough to check a box exists and not enough to
 * fix one: a value cited twice looked identical to a value cited once, and a
 * box in the wrong place could only be corrected by editing JSON by hand.
 *
 * So the numbers are on screen. Each row is one region, its four coordinates
 * editable, with an eye that toggles it alone on the page. Typing and
 * dragging edit the same values from opposite ends -- the numbers are the
 * truth, and the rectangle is a view of them.
 *
 * Normalised 0..1 with the origin top-left, which is what the label stores.
 * Shown as percentages because a pharmacist reading "0.4312" learns nothing,
 * while "43.1%" is a position on a page.
 */

export interface RegionRowsProps {
  readonly path: string
  readonly regions: readonly LabelRegion[]
  /** Which region is shown alone, as `path#index`. */
  readonly shown: string | undefined
  readonly onToggle: (ref: string) => void
  readonly onChange: (index: number, region: LabelRegion) => void
  /**
   * How many pages the case has.
   *
   * The page field appears only when there is a choice to make. Every case in
   * the set today is a single page, so the column read "1" on every row --
   * width spent saying nothing, on a row that must not wrap.
   */
  readonly pageCount: number
  readonly readOnly?: boolean | undefined
}

/** Percent in, fraction out, clamped: a box cannot leave the page. */
function fraction(percent: string): number {
  const value = Number(percent)
  if (!Number.isFinite(value)) return 0
  return Math.min(1, Math.max(0, value / 100))
}

const percent = (value: number): string => (value * 100).toFixed(1)

/** Zero area means nobody has boxed this: drawn nowhere, measured as absent. */
export const isEmptyRegion = (r: LabelRegion): boolean => r.w <= 0 || r.h <= 0

const EMPTY: LabelRegion = { page: 0, x: 0, y: 0, w: 0, h: 0 }

export function RegionRows({
  path,
  regions,
  shown,
  onToggle,
  onChange,
  pageCount,
  readOnly = false,
}: RegionRowsProps) {
  // A field with nothing recorded still shows one row to type or drag into.
  /*
   * One region per field, always shown.
   *
   * Adding a second was possible while removing one was, and neither is now:
   * clearing zeroes the row rather than deleting it, so a field always has
   * exactly one box or an empty one. A field genuinely cited twice is rare
   * enough that the second citation is worth losing to keep this a fixed row
   * of numbers rather than a list to manage.
   */
  const shownRegions = regions.length === 0 ? [EMPTY] : regions.slice(0, 1)

  return (
    <div className="flex w-max flex-col gap-1">
      {/*
        Always a row, even for a field nobody has boxed.
        
        An empty state that had to be clicked before any numbers appeared made
        the common case -- box this value -- a two-step, and made a field with
        no region look like a field with no *support* for one. A row of zeros
        is visibly empty and immediately editable.
      */}
      {shownRegions.map((region, i) => {
        const ref = `${path}#${i}`
        const isShown = shown === ref
        return (
          <div key={i} className="flex flex-nowrap items-center gap-1">
            {pageCount > 1 && (
              <label className="flex shrink-0 items-center gap-0.5">
                <span className="text-tTiny text-text-weak uppercase">pág</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min="1"
                  step="1"
                  aria-label={`Página da região ${i + 1} de ${path}`}
                  value={region.page + 1}
                  readOnly={readOnly}
                  onChange={(e) =>
                    onChange(i, { ...region, page: Math.max(0, Number(e.target.value) - 1) })
                  }
                  className="rounded-base border-stroke-weak bg-background-raised text-tTiny text-text-strong w-10 shrink-0 border px-1 py-0.5 text-right tabular-nums"
                />
              </label>
            )}

            {(['x', 'y', 'w', 'h'] as const).map((axis) => (
              <label key={axis} className="flex shrink-0 items-center gap-0.5">
                <span className="text-tTiny text-text-weak uppercase">{axis}</span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  max="100"
                  aria-label={`${axis} da região ${i + 1} de ${path}`}
                  value={percent(region[axis])}
                  readOnly={readOnly}
                  onChange={(e) => onChange(i, { ...region, [axis]: fraction(e.target.value) })}
                  className="rounded-base border-stroke-weak bg-background-raised text-tTiny text-text-strong w-12 shrink-0 border px-1 py-0.5 text-right tabular-nums"
                />
              </label>
            ))}

            {/*
              The eye is a toggle, not a jump. Clicking the same region again
              returns to showing every box, which is the view you want between
              corrections -- one where you can see what is still unboxed.
            */}
            {/*
              One control in two statuses, not two controls. It was a button
              whose label changed between "ver" and "na receita", which reads
              as a different action rather than the same one already on --
              and the label made a row of numbers twice as wide as it needed
              to be.

              A pin rather than an eye: pressed, it holds this one region on
              the page and keeps it there. An eye suggests looking, which is
              what the whole overlay already does.
            */}
            <span className="flex shrink-0 items-center gap-0.5">
              <Button
                variant={isShown ? 'secondary' : 'tertiary'}
                tone={isShown ? 'brand' : 'neutral'}
                size="small"
                leadingIcon={icons.pin}
                aria-pressed={isShown}
                aria-label={isShown ? 'Mostrar todas as regiões' : 'Mostrar só esta região'}
                title={isShown ? 'Mostrar todas as regiões' : 'Mostrar só esta região'}
                onClick={() => onToggle(ref)}
              />

              {/*
              Clear, not remove. The row stays and its numbers go to zero,
              which is what "this value has no box" looks like everywhere
              else -- a zero-area region is drawn nowhere and measured as
              absent. Removing the row instead left the field with no visible
              way back to having one.
            */}
              {!readOnly && (
                <Button
                  variant="tertiary"
                  tone="neutral"
                  size="small"
                  leadingIcon={icons.clear}
                  aria-label={`Limpar a região ${i + 1}`}
                  title="Zerar esta região"
                  onClick={() => onChange(i, { ...EMPTY, page: region.page })}
                />
              )}
            </span>
          </div>
        )
      })}
    </div>
  )
}
